import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { audioPath } from 'constants/audioConstants';
import {
  SEAT_CONTAINER_HEIGHT,
  SEAT_CONTAINER_WIDTH,
  TIME_PROGRESS_BAR_THICKNESS
} from 'constants/gameConstants';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { useAppSelector } from 'hooks';
import useSound from 'hooks/useSound';
import { useTableSelector } from 'hooks/useTableSelector';
import { clearCountdown, holdsCountdownFloor, publishCountdown } from 'utils/attentionCues';
import { getPercentage } from 'utils/functions';
import { recordSfx } from 'utils/sfxTrace';

import { getProgressColor, TimerPropType } from './';

const DRIFT_THRESHOLD = 1; // seconds — only resync if server diverges by more than this

// Wall-clock receipt time of each server tick, shared by every SeatTimer
// instance. A timer mounted MID-turn (the bet tray's presentation twin,
// EN-10420) would otherwise start from the tick VALUE alone — up to a full
// second stale by the time the player opens the tray — and free-run that far
// behind the seat's ring for the rest of the turn, because the mid-turn
// resync only corrects drift beyond DRIFT_THRESHOLD (1s) and only forward.
// Recording when each tick ARRIVED lets a late-mounting instance add the wall
// time already spent since receipt and land in step with the seat's tween.
// Keyed per turn+tick; identical tuples from different tables would compute
// identical elapsed anyway, so cross-table collisions are harmless.
const tickReceiptMs = new Map<string, number>();
export const recordTickReceipt = (start: number, current: number) => {
  const key = `${start}|${current}`;
  if (!tickReceiptMs.has(key)) {
    if (tickReceiptMs.size > 32) tickReceiptMs.clear();
    tickReceiptMs.set(key, performance.now());
  }
};
export const secsSinceTickReceipt = (start: number, current: number) => {
  const at = tickReceiptMs.get(`${start}|${current}`);
  return at === undefined ? 0 : Math.max(0, (performance.now() - at) / 1000);
};

// Seconds remaining at/below which the acting player hears the per-second
// "running out of time" beep on their OWN turn (Unity parity: UIPlayer's
// warningSoundDuration = 5, played each second at 5,4,3,2,1).
export const TURN_WARNING_SECS = 5;

/**
 * Whether to play the per-second turn-warning beep this tick. Fires only on the
 * local player's own active turn, for the last few seconds — mirrors Unity's
 * PlayWaitWarning (duration <= warningSoundDuration && duration > 0). Exported
 * for unit testing.
 */
export const shouldPlayTurnWarning = (
  secs: number,
  isOwnActiveTurn: boolean,
  thresholdSecs: number = TURN_WARNING_SECS
): boolean => isOwnActiveTurn && secs > 0 && secs <= thresholdSecs;

// Identity for the countdown floor's per-copy focus claims. A table is mounted
// more than once (carousel panel + grid cell, plus the bet tray's presentation
// twin), and attentionCues has to tell "the other copy wrote unfocused" (must
// not release the panel's claim) apart from "the SAME copy switched to
// unfocused" (the player left the table mid-turn — must release it). Ids only
// need to be distinct within a tab's lifetime; 0 is reserved as "no copy".
let nextCountdownCopyId = 1;

const SeatTimer = ({
  children,
  start,
  current,
  target,
  isStartProgress,
  size,
  presentationOnly = false,
  ownsWarningCue
}: TimerPropType) => {
  const [progress, setProgress] = useState(0);
  // Seeded from the clock, not 0. startTween runs in a passive effect, so the
  // FIRST COMMITTED FRAME renders this state — and 0 on a countdown reads as
  // "expired" on the one cue whose job is to say there is still time. It mostly
  // went unnoticed on the seat (mounted long before the turn) but the vote dial
  // and the bet tray's twin both mount mid-window, straight onto that frame.
  // Guarded because this is the FIRST COMMITTED FRAME and nothing corrects it
  // before the player sees it: a payload briefly missing `target`, `current` or
  // `start` (initial hydration, a reconnect, the /demo-table preview) turns the
  // arithmetic NaN, and NaN survives Math.max/Math.ceil all the way to the
  // overlay, which then literally renders "NaN". 0 is the same safe frame the
  // state used to be seeded with, so the fallback is strictly the old behaviour.
  const [countdown, setCountdown] = useState(() => {
    const remaining = Math.ceil(target - Math.max(current - start, 0));

    return Number.isFinite(remaining) ? Math.max(remaining, 0) : 0;
  });
  // This copy's identity in the countdown floor (see nextCountdownCopyId),
  // assigned lazily so instances that never publish never consume one.
  const copyIdRef = useRef(0);
  // Has THIS instance published into the countdown floor this turn? Gates
  // clearCountdown in stopTimer — see the comment there.
  const hasPublishedRef = useRef(false);
  // crossTable: the countdown pulls the player to a table they are not
  // watching, so it opts out of the active-table sound gate (EN-10577 AC2).
  // It is additionally exclusive — see the floor logic below.
  const [playPlayerTimeWarning] = useSound(audioPath.playerTimeWarning, { crossTable: true });
  // Is the player looking at this table? Read through a ref because the beat
  // fires inside the long-lived interval closure. The focused table always
  // owns its own countdown — see attentionCues.holdsCountdownFloor.
  const isFocusedTable = useIsActiveTable();
  const isFocusedRef = useRef(isFocusedTable);
  isFocusedRef.current = isFocusedTable;
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const game = useTableSelector((table) => table.game);
  const currentPlayerSeatId = useTableSelector((table) => table.currentPlayer.seatId);
  const proxyRef = useRef({ progress: 0 });
  const circleRef = useRef<SVGCircleElement>(null);
  // The countdown is driven by ONE setInterval at 20fps, not by GSAP and not
  // by requestAnimationFrame - see the comment in startTween.
  const intervalRef = useRef<number | null>(null);

  // Track the start value that initiated the current tween so we know when a new turn begins
  const activeStartRef = useRef<number | null>(null);

  // Last whole-second we beeped, so a mid-turn resync re-entering the same second
  // doesn't double-play the warning. Reset each new turn.
  const warnedSecRef = useRef<number | null>(null);
  // Latest "is this the local player's own active turn?" for the tween's onUpdate
  // closure (the warning cue only plays for the user's own countdown). Mirrored
  // in a layout effect rather than during render so render stays pure; the GSAP
  // ticker only reads the ref after commit, so it never observes a stale value.
  // The seat says whose countdown this is when it knows (EN-10920: during a
  // multi-run vote the seat being asked is not `game.move`); otherwise the
  // betting-turn rule decides.
  const ownActiveTurnRef = useRef(false);
  useLayoutEffect(() => {
    ownActiveTurnRef.current =
      !!isStartProgress && (ownsWarningCue ?? String(game.move) === String(currentPlayerSeatId));
  });

  const offset = TIME_PROGRESS_BAR_THICKNESS * 2.25;
  const radius = (size - TIME_PROGRESS_BAR_THICKNESS * 2) / 2;
  const circumference = 2 * Math.PI * radius;

  // Stop the local ticker and nothing else. startTween restarts through THIS,
  // not stopTimer: a tween restart — the mid-turn drift resync below, or the
  // bet tray's presentation twin mounting — is not the end of the turn, and
  // clearing the countdown floor there opened a ~1s hole (until the next
  // whole-second beat republished) in which a background table saw no claim
  // and beeped over the player's own countdown.
  const stopTicker = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const stopTimer = () => {
    stopTicker();
    // The turn is over however it ended (acted, timed out, unmounted) — hand
    // the countdown floor to the next-closest table (EN-10577 AC7). But only
    // the instance that PUBLISHED the claim may clear it: clearCountdown is
    // table-scoped, and every seat's timer on this table (each mounted twice,
    // plus the tray's presentation twin) runs stopTimer, while only the acting
    // seat's copies ever publish. Without the gate, any foreign instance's
    // stop wiped the live claim mid-turn.
    //
    // And the clear is PER COPY: both mounted copies of the acting seat
    // publish, so this releases only this copy's claim, and the table's entry
    // survives while the twin still publishes — one copy unmounting
    // mid-countdown (grid re-layout, table closing) no longer wipes the live
    // claim (dev review #3747 round 2, residual). A genuine turn end still
    // clears fully, because BOTH copies stop and release.
    if (hasPublishedRef.current) {
      hasPublishedRef.current = false;
      // hasPublishedRef guarantees copyIdRef was assigned at first publish.
      clearCountdown(game.gameID, copyIdRef.current);
    }
  };

  // The ring updates 20x/sec: on a 15s+ sweep that is visually
  // indistinguishable from per-frame motion (~0.3% of arc per step).
  const TIMER_TICK_MS = 50;

  // Start or restart the countdown from a given elapsed position.
  //
  // PERF - this is a setInterval on purpose. Do NOT "modernize" it to GSAP or
  // requestAnimationFrame: on Android WebView a pending rAF (which is also
  // what GSAP's ticker is) forces the compositor to produce frames at the
  // display refresh rate for as long as it is scheduled, even when nothing
  // changes on screen. The previous GSAP implementation kept the entire frame
  // pipeline hot for all ~15s of every turn and was measured as the largest
  // single contributor to table-view CPU/overheating (214% -> ~110% idle on a
  // Galaxy S24+ after this change). An interval only produces frames when it
  // actually writes a style. See
  // docs/investigations/20260819-game-client-overheating.md (section 8d).
  const startTween = (elapsed: number) => {
    stopTicker();

    const clamped = Math.max(0, Math.min(elapsed, target));

    proxyRef.current.progress = clamped;
    const startedAtMs = performance.now();

    // React state (countdown number + color) still updates only on
    // integer-second changes - ~1 render/sec per active player.
    let lastSecs = Number.NaN;
    const tick = () => {
      const progress = Math.min(clamped + (performance.now() - startedAtMs) / 1000, target);
      proxyRef.current.progress = progress;

      if (circleRef.current) {
        // Inline style beats the React-rendered presentation attribute in the
        // cascade, mirroring what the GSAP tween used to do.
        circleRef.current.style.strokeDashoffset = `${(progress / target) * circumference}px`;
      }

      const secs = Math.max(Math.ceil(target - progress), 0);
      if (secs !== lastSecs) {
        lastSecs = secs;
        const pct = getPercentage(progress, target);
        setProgress(pct);
        setCountdown(secs);

        // Per-second "running out of time" beep on the local player's own turn
        // (Unity parity: PlayWaitWarning at 5,4,3,2,1), deduped per second.
        // presentationOnly instances never cue — the seat's timer owns audio.
        if (
          !presentationOnly &&
          secs !== warnedSecRef.current &&
          shouldPlayTurnWarning(secs, ownActiveTurnRef.current)
        ) {
          warnedSecRef.current = secs;
          // ONE COUNTDOWN AT A TIME (EN-10577 AC7). Several of the player's
          // tables can be counting down together, and hearing all of them is
          // what makes multi-tabling feel chaotic. Publish this table's
          // time-to-zero, then beep only if this table holds the floor: the
          // table the player is looking at always owns its own countdown
          // (EN-10585 AC4), and the deadline race arbitrates the background
          // tables only. A claim expires at its own deadline, so a stalled
          // table can never permanently silence the rest.
          const nowMs = Date.now();
          if (copyIdRef.current === 0) copyIdRef.current = nextCountdownCopyId++;
          publishCountdown(game.gameID, secs, nowMs, isFocusedRef.current, copyIdRef.current);
          hasPublishedRef.current = true;
          // Dedupe is useSound's job, passed per second because the cue's
          // identity changes every beat. Claiming HERE lost beats: whichever of
          // the table's mounted copies claimed first burned the claim even when
          // its own play was then blocked (arrival snap, audio still
          // resolving), and the other copy was refused. Intermittently four
          // beeps instead of five. Same failure the win cue had.
          const mayBeep = holdsCountdownFloor(game.gameID, secs, nowMs, isFocusedRef.current);
          if (mayBeep) {
            playPlayerTimeWarning({ dedupeKey: `warn:${secs}` });
          } else {
            // A suppressed beat must leave a trace, or "the countdown skipped"
            // is forever indistinguishable from a bug (SFX Lab, Live trace).
            recordSfx({
              src: 'player_time_warning',
              blockedBy: 'countdown-floor',
              gameID: String(game.gameID),
              crossTable: true,
              isActiveTable: isFocusedRef.current
            });
          }
          // Parity: buzz on odd seconds (5, 3, 1) where the platform supports it.
          if (mayBeep && secs % 2 === 1 && typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(80);
          }
        }
      }

      if (progress >= target) stopTimer();
    };

    tick(); // paint the ring/label at the start position immediately
    intervalRef.current = window.setInterval(tick, TIMER_TICK_MS);
  };

  // New turn: start or target changed — always restart the tween
  useEffect(() => {
    if ((!isMaintenance || game.tabletype === 's') && isStartProgress) {
      // First instance to see this tick stamps its arrival; a mid-turn mount
      // finds the seat's stamp and starts already caught up (see tickReceiptMs).
      recordTickReceipt(start, current);
      const elapsed = Math.max(current - start, 0) + secsSinceTickReceipt(start, current);
      activeStartRef.current = start;
      warnedSecRef.current = null;
      startTween(elapsed);
    } else {
      // Timer stopped — clean up. stopTimer also hands the countdown floor on
      // if this instance held it (EN-10577 AC7).
      stopTimer();
      // NO TIMEOUT CUE here, deliberately. player_out_of_time was removed
      // under EN-10585 AC6: it was the "bell" the owner chased (8/26, Howl
      // logging) — the longest and one of the loudest sounds in the app,
      // ringing on every bot fold once it went crossTable — and a third
      // announcement of a thing already said twice (the countdown before it,
      // the fold cue right after). The asset stays in assets/sounds; restoring
      // it is one import away.
    }

    return () => {
      stopTimer();
    };
  }, [start, target, isStartProgress, isMaintenance, game.tabletype]);

  // Server time updates mid-turn — only resync if drifted beyond threshold
  useEffect(() => {
    // Stamp every tick's arrival (even when this instance skips below) so a
    // timer mounting later in the turn can compute true elapsed time.
    recordTickReceipt(start, current);
    // Skip if this is a new turn (handled by the effect above) or timer isn't running
    if (!isStartProgress || start !== activeStartRef.current || intervalRef.current === null)
      return;

    const serverElapsed = Math.max(current - start, 0);
    const localElapsed = proxyRef.current.progress;
    const drift = Math.abs(serverElapsed - localElapsed);

    if (drift > DRIFT_THRESHOLD) {
      // Server diverged — resync without killing the visual flow
      // Only jump forward (server ahead), never backward
      if (serverElapsed > localElapsed) {
        startTween(serverElapsed);
      }
      // If server is behind us by >1s, that's unusual — still don't jump back,
      // just let the local tween continue. It'll correct on the next turn.
    }
  }, [current]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        height: SEAT_CONTAINER_HEIGHT,
        width: SEAT_CONTAINER_WIDTH,
        overflow: 'visible'
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {children}
      </Box>
      {/* Countdown number overlay on avatar */}
      {isStartProgress && (
        <div style={countdownOverlayStyle}>
          <span
            style={{
              ...countdownTextStyle,
              color: getProgressColor(progress)
            }}>
            {countdown}
          </span>
        </div>
      )}
      {isStartProgress && (
        <svg
          data-seat-timer-ring={presentationOnly ? 'presentation' : 'seat'}
          width={size}
          height={size}
          style={{
            position: 'absolute',
            top: -offset,
            left: -offset,
            transform: 'rotate(-90deg)',
            zIndex: 1
          }}>
          {/* Background ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill='none'
            stroke='rgba(0,0,0,0.3)'
            strokeWidth={TIME_PROGRESS_BAR_THICKNESS * 2}
          />
          {/* Animated progress ring — GSAP drives strokeDashoffset continuously */}
          <circle
            ref={circleRef}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill='none'
            stroke={getProgressColor(progress)}
            strokeWidth={TIME_PROGRESS_BAR_THICKNESS * 2}
            strokeDasharray={circumference}
            strokeDashoffset={0}
            strokeLinecap='round'
            style={{ transition: 'stroke 1s ease' }}
          />
        </svg>
      )}
    </Box>
  );
};

export default SeatTimer;

const countdownOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  borderRadius: '50%',
  zIndex: 5,
  pointerEvents: 'none'
};

const countdownTextStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 800,
  fontFamily: "'Outfit', sans-serif",
  textShadow: '0 0 8px rgba(0,0,0,0.8), 0 0 16px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.9)',
  WebkitTextStroke: '1px rgba(0, 0, 0, 0.3)'
};
